import { Badge, Box, Center, Checkbox, Flex, IconButton, ProgressCircle, Spinner, Stack, Table, Text } from '@chakra-ui/react';
import { FiTrash2 } from 'react-icons/fi';
import { formatDocumentTitle, setDocumentTitle } from '../../../utils/documentTitle';
import { Lead } from '@opanuj/shared/types';
import { ref, remove, update } from 'firebase/database';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDatabase } from '../../../hooks/useDatabase';
import { useLeadsList } from '../../../hooks/useLeads';
import { useNavigationBadges } from '../../../navigation/NavigationBadgesContext';

const pageTitle = formatDocumentTitle('Zgłoszenia');

const LeadsPage = () => {
  const database = useDatabase();
  const { setBadgeCount } = useNavigationBadges();
  const [leads, loading, error] = useLeadsList();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(setDocumentTitle(pageTitle)); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleLeads = useMemo(
    () => (leads ?? []).filter((lead) => !lead.archived),
    [leads],
  );

  useEffect(() => {
    setBadgeCount('leads', visibleLeads.length);
  }, [setBadgeCount, visibleLeads.length]);

  const toggleArchived = async (lead: Lead, checked: boolean) => {
    const path = `leads/${lead.id}`;
    await update(ref(database, path), { archived: checked || null });
  };

  const deleteLead = useCallback(async (lead: Lead) => {
    const label = lead.name || lead.email || lead.phone || 'bez danych';
    const shouldDelete = window.confirm(`Czy na pewno chcesz usunąć zgłoszenie ${label}?`);

    if (!shouldDelete) {
      return;
    }

    setPendingDeleteId(lead.id);

    try {
      await remove(ref(database, `leads/${lead.id}`));
    } catch (err) {
      console.error('Lead delete failed', err);
    } finally {
      setPendingDeleteId((current) => (current === lead.id ? null : current));
    }
  }, [database]);

  if (loading) {
    return (
      <Center my={12}>
        <ProgressCircle.Root value={null}>
          <ProgressCircle.Circle>
            <ProgressCircle.Track />
            <ProgressCircle.Range stroke="orange.400" />
          </ProgressCircle.Circle>
        </ProgressCircle.Root>
      </Center>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Text color="red.500">Nie udało się wczytać zgłoszeń.</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, lg: 6 }}>
      <Stack gap={4}>
        <Flex justify="flex-end" align="center" wrap="wrap" gap={3}>
          <Badge colorPalette="orange" variant="subtle" rounded="full" px={3} py={1}>
            {visibleLeads.length} aktywnych
          </Badge>
        </Flex>

        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
          <Table.ScrollArea maxH="calc(100vh - 220px)" style={{ overflowY: 'auto' }}>
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader fontWeight="bold">Data</Table.ColumnHeader>
                  <Table.ColumnHeader fontWeight="bold">Imię</Table.ColumnHeader>
                  <Table.ColumnHeader fontWeight="bold">Telefon</Table.ColumnHeader>
                  <Table.ColumnHeader fontWeight="bold">Email</Table.ColumnHeader>
                  <Table.ColumnHeader fontWeight="bold">Notatki</Table.ColumnHeader>
                  <Table.ColumnHeader fontWeight="bold" textAlign="center">Ogarnięte</Table.ColumnHeader>
                  <Table.ColumnHeader fontWeight="bold" textAlign="center">Akcje</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(leads ?? []).length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={7}>
                      <Text py={4} textAlign="center" color="gray.500">Brak zgłoszeń.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  (leads ?? []).map((lead) => (
                    <Table.Row key={lead.id}>
                      <Table.Cell>{lead.createdAt ? new Date(lead.createdAt).toLocaleString('pl-PL') : '—'}</Table.Cell>
                      <Table.Cell>{lead.name || '—'}</Table.Cell>
                      <Table.Cell>{lead.phone || '—'}</Table.Cell>
                      <Table.Cell>{lead.email || '—'}</Table.Cell>
                      <Table.Cell maxW="360px">
                        <Text whiteSpace="pre-wrap">{lead.notes || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Checkbox.Root
                          checked={Boolean(lead.archived)}
                          onCheckedChange={({ checked }) => toggleArchived(lead, checked === true)}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <IconButton
                          aria-label={`Usuń zgłoszenie ${lead.name || lead.email || lead.phone || 'bez danych'}`}
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          type="button"
                          disabled={pendingDeleteId === lead.id}
                          onClick={() => deleteLead(lead)}
                        >
                          {pendingDeleteId === lead.id ? <Spinner size="xs" /> : <FiTrash2 size={14} />}
                        </IconButton>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        </Box>
      </Stack>
    </Box>
  );
};

export default LeadsPage;
